import {t, Trans} from '@lingui/macro';
import {isNumber} from 'lodash';
import React, {FC, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {CheckBox} from '../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import requiredTaggingIllustration from '../../../../assets/images/requiredTagging.png';
import {ComplexTooltipContent} from '../../../../components/common/tooltips/complexTooltipContent';
import {TagModel} from '../../../../models/api/tagModel';
import {useBaseEditorContext} from '../../../editors/baseEditorContext';
import {getEditorErrorLabel} from '../../../editors/commons/editorErrors';
import {SelectedDropdownItem} from '../dropdowns/dropdownWithExpressions';
import {EditorStateActions} from '../hooks/useEditorState';
import {TagsParameter} from '../parameters/tagParameter';
import {RequiredTaggingEditorErrorsEnum} from '../recipes/requiredTagging/requiredTaggingEditorErrors';
import {
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';

/*
 * Types.
 */

interface TagListCardProps {
  tagIds: ReadonlyArray<number>;
  name: string;
  isOneTagRequired: boolean;
  isEditing: boolean;
  clientId: string;
  shouldHideError: boolean;

  onTagIdsChange: HandlerOf<number[]>;
  onNameChange: HandlerOf<string>;
  onIsOneTagRequiredChange: HandlerOf<boolean>;
  isConflicting: boolean;
  onCancel?: Handler;
  onValidation: EditorStateActions['onValidation'];
}

/*
 * Styles.
 */

const StyledTextBoxContainerDiv = styled.div`
  width: 224px;
`;

const StyledCheckBoxContainerDiv = styled.div`
  padding-bottom: 8px;
`;

const SecondLineWrapper = styled.div`
  height: 16px;
  padding-bottom: 8px;
  ${flexCenter}
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: inline-block;
`;

/*
 * Component.
 */

export const TagListCard: FC<TagListCardProps> = (props) => {
  const {
    onCancel,
    clientId,
    tagIds,
    name,
    shouldHideError,
    isOneTagRequired,
    isConflicting,
    onTagIdsChange,
    onNameChange,
    onIsOneTagRequiredChange,
    onValidation,
    isEditing,
  } = props;
  const localize = useLocalize();
  const [shouldExpandCollapsableParameters, setShouldExpandCollapsableParameters] = useState(isEditing);
  const {hasHighlightErrorsFlag} = useBaseEditorContext();

  const onChange = (updatedTagIds: OrNull<ReadonlyArray<SelectedDropdownItem<TagModel>>>) => {
    if (updatedTagIds) {
      onTagIdsChange(updatedTagIds.filter(isNumber));
    }
  };

  const onToggle = (isOpen: boolean) => {
    if (isOpen || tagIds.length === 0) {
      return;
    }

    setShouldExpandCollapsableParameters(true);
  };

  const maybeCardError = useMemo(() => {
    if (hasHighlightErrorsFlag && tagIds.length === 0) {
      return RequiredTaggingEditorErrorsEnum.MISSING_TAGS;
    }

    return undefined;
  }, [hasHighlightErrorsFlag, tagIds]);

  const maybeNameError = useMemo(() => {
    if (hasHighlightErrorsFlag && name === '') {
      // todo change for missing name error
      return RequiredTaggingEditorErrorsEnum.INVALID_TAG_LIST;
    }

    if (isConflicting) {
      return RequiredTaggingEditorErrorsEnum.TAG_LIST_NAME_CONFLICT;
    }

    return undefined;
  }, [isConflicting, hasHighlightErrorsFlag, name]);

  useEffect(() => {
    if (hasHighlightErrorsFlag) {
      onValidation(clientId, maybeCardError ?? maybeNameError);
    }
  }, [hasHighlightErrorsFlag, clientId, onValidation, maybeCardError, maybeNameError]);

  const renderTooltip = () => (
    <ComplexTooltipContent
      maxWidth={220}
      title={
        <Trans id="required-tagging-editor-tag-list-tooltip-title">
          Why do I need to name the list of tags I select?
        </Trans>
      }
      body={
        <Trans id="required-tagging-editor-tag-list-tooltip-content">
          <p>
            This name is how your teammates access the required tag list when applying tags from the inbox.
          </p>
          <p>
            <b>Example:</b> You need all inbounds to have a client tag. In this rule, select all of your
            client tags, and then name that list “Clients”. Your team will see “Clients” at the top of the
            tags dropdown.
          </p>
        </Trans>
      }
      imageUrl={requiredTaggingIllustration}
      imageDescription={localize(
        t('required-tagging-editor-tag-list-imagealt')`Required Tagging illustration`,
      )}
      imageRounded
    />
  );

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>
        <Trans id="required-tagging-editor-teammates-must-tag">Teammates must tag conversations with</Trans>
      </MultilineRuleParameterCardDefaultTitleStyled>
      <TagsParameter
        selectedItems={tagIds}
        onChange={onChange}
        onToggle={onToggle}
        selectedItemsSeparator={<>,</>}
        maxItemsToRender={1}
      />
    </>
  );

  const tagListNameParameter = (
    <StyledTextBoxContainerDiv key="taglistname">
      <TextBox
        label={<Trans id="required-tagging-editor-tag-list-name">Tag list name</Trans>}
        additionalLabelInfo={<StyledTooltipCoordinator render={renderTooltip} placement="top" />}
        value={name}
        placeholder={localize(
          t('required-tagging-editor-tag-list-name-placeholder')`e.g. “Customers”, “Regions”…`,
        )}
        onChange={onNameChange}
        hasError={!shouldHideError && Boolean(maybeNameError)}
        errorMessage={
          maybeNameError && !shouldHideError ? localize(getEditorErrorLabel(maybeNameError)) : undefined
        }
        isRequired
        shouldAutoFocus={!isEditing}
      />
    </StyledTextBoxContainerDiv>
  );

  const oneTagRequiredParameter = (
    <StyledCheckBoxContainerDiv key="onetagrequired">
      <CheckBox isChecked={isOneTagRequired} onChange={() => onIsOneTagRequiredChange(!isOneTagRequired)}>
        <Trans id="required-tagging-editor-one-tag-required">Only one tag in this list can be applied</Trans>
      </CheckBox>
    </StyledCheckBoxContainerDiv>
  );

  return (
    <RuleMultilineParameterCard
      icon={<Icon name="tagFilled" />}
      title={title}
      onCancel={onCancel}
      collapsableParameters={[tagListNameParameter, oneTagRequiredParameter]}
      shouldExpandCollapsableParameters={shouldExpandCollapsableParameters}
      errorMessage={
        maybeCardError && !shouldExpandCollapsableParameters && !shouldHideError
          ? localize(getEditorErrorLabel(maybeCardError))
          : undefined
      }
    >
      <SecondLineWrapper>
        <Trans id="required-tagging-editor-before-move-archive">before they move or archive it</Trans>
      </SecondLineWrapper>
    </RuleMultilineParameterCard>
  );
};
